import React, { useState, useEffect, useRef } from 'react';

export interface LogoProps {
  background?: 'light' | 'dark' | 'auto';
  scrollThreshold?: number;
  className?: string;
  onClick?: () => void;
  alt?: string;
}

const Logo: React.FC<LogoProps> = ({ 
  background = 'auto', 
  scrollThreshold = 100,
  className = 'h-4 w-auto',
  onClick,
  alt = 'AXL Logo'
}) => {
  const [logoSrc, setLogoSrc] = useState<string>('/axl_logo_1000_white.svg');
  const [isScrolled, setIsScrolled] = useState(false);
  const logoRef = useRef<HTMLImageElement>(null);

  // Helper function to determine if a color is dark
  const isColorDark = (color: string): boolean => {
    const rgbMatch = color.match(/\d+/g);
    if (!rgbMatch || rgbMatch.length < 3) return false;
    
    const r = parseInt(rgbMatch[0]);
    const g = parseInt(rgbMatch[1]);
    const b = parseInt(rgbMatch[2]);
    
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
  };

  // Handle scroll state
  useEffect(() => {
    if (background !== 'auto') return;

    const handleScroll = () => {
      const scrolled = window.scrollY > scrollThreshold;
      setIsScrolled(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [background, scrollThreshold]);

  // Detect background color and determine logo
  useEffect(() => {
    console.log('Logo useEffect running, background:', background);
    
    if (background === 'light') {
      const newSrc = '/axl_logo_1000_white.svg';
      console.log('Setting logo to white:', newSrc);
      setLogoSrc(newSrc);
      return;
    }
    
    if (background === 'dark') {
      const newSrc = '/axl_logo_1000_black.svg';
      console.log('Setting logo to black:', newSrc);
      setLogoSrc(newSrc);
      return;
    }

    // Auto mode: detect background color
    if (logoRef.current) {
      const element = logoRef.current;
      const parent = element.closest('[class*="bg-"]') || 
                     element.parentElement?.closest('[class*="bg-"]') ||
                     document.body;
      
      const computedStyle = window.getComputedStyle(parent as Element);
      const bgColor = computedStyle.backgroundColor;
      const isDarkBackground = isColorDark(bgColor);
      
      const newSrc = isDarkBackground 
        ? '/axl_logo_1000_white.svg' 
        : '/axl_logo_1000_black.svg';
      
      console.log('Auto mode - bgColor:', bgColor, 'isDark:', isDarkBackground, 'logo:', newSrc);
      setLogoSrc(newSrc);
    }
  }, [background, isScrolled]);

  // Log when logoSrc changes
  useEffect(() => {
    console.log('Logo src changed to:', logoSrc);
  }, [logoSrc]);

  return (
    <img 
      ref={logoRef}
      src={logoSrc} 
      alt={alt} 
      className={className}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      onError={(e) => {
        console.error('Logo failed to load:', logoSrc);
        console.error('Image error:', e);
      }}
      onLoad={() => {
        console.log('Logo loaded successfully:', logoSrc);
      }}
    />
  );
};

export default Logo;